<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { convertSeconds, kmToMile, tidy, formatXSSDetails, calculateCompletedXSS, XSSDeviationCategory, xssDeviationToCategory, type XSSDetails, type XSSDeviationDetails } from '@/Util/utils';
  import { format } from 'date-fns';
  import { ActivityTag } from '@/Common/ActivityTag';
  import TagWrapper from '@/Common/TagWrapper.svelte';
  import XSSDisplay from '../../Common/XSSDisplay.svelte';
  import { cn } from '@/lib/utils';
  import { activitiesForDate } from '@/Singletons/ActivitiesForDateWritable';

  export let activity: Exercise;
  export let boldTitle: boolean = false;

  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert._displayUnits == 'imperial';

  const activityTag = ActivityTag('small');

  $: activityTags = [
    activityTag.forecast(
      { ...activity, state: activity.state },
      Activity.isRest({ ...activity, state: activity.state })
    ),
    !Activity.isForecast({ ...activity, state: activity.state }) ? activityTag.planned(activity) : null,
  ].filter((tag) => tag)

  $: xssDetails = {
    xss: activity.xss ?? 0,
    xlss: activity.xlss ?? 0,
    xhss: activity.xhss ?? 0,
    xpss: activity.xpss ?? 0,
  }
  $: formattedXSSDetails = formatXSSDetails(xssDetails);

  let formattedCompletedXSS: XSSDetails<string> | null = null;
  let xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null = null;
  $: {
    let matchedActivities = $activitiesForDate?.activities?.filter(
      a => Activity.isMatchedAtoB(a, activity) && Activity.isInCalculations(a)
    );
    if (!matchedActivities || matchedActivities.length == 0) {
      formattedCompletedXSS = null;
      xssDeviationCategory = null;
    } else {
      let completedXSS = calculateCompletedXSS(activity, matchedActivities);
      if (completedXSS) formattedCompletedXSS = completedXSS ? formatXSSDetails(completedXSS) : null;
      else formattedCompletedXSS = null;

      if (xssDetails && completedXSS) xssDeviationCategory = xssDeviationToCategory(xssDetails, completedXSS);
      else xssDeviationCategory = null;
    }
  }

</script>

<div class="mb-2 rounded border border-border p-2 text-xs bg-gray-50">
  <div class="flex items-center justify-between">
    <div class="flex items-center gap-2">
      <TagWrapper tags={activityTags} />
      <span class={cn( boldTitle ? 'font-semibold' : '' )}>{activity.name}</span>
    </div>
    {#if formattedCompletedXSS}
      <i class={cn('fa-solid ml-2 fa-lg',
        xssDeviationCategory.xss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
        xssDeviationCategory.xss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
        'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
      )} />
    {:else}
      <div class="text-gray-600 whitespace-nowrap ml-2">{format(new Date(activity.start_date), 'hh:mm aa')}</div>
    {/if}
  </div>
  {#if !formattedCompletedXSS}
    <div class="mt-1 flex flex-wrap items-center gap-2 text-gray-700">
      <span>{convertSeconds(activity.duration ?? activity.adu)}</span>
      <span>{tidy(useFreedomUnits ? kmToMile(activity.distance ?? 0) : (activity.distance ?? 0))} {useFreedomUnits ? 'mi' : 'km'}</span>
      <span class="ml-auto py-0.5">
        XSS:
        <XSSDisplay className='font-medium text-xs'
          xss={formattedXSSDetails}
        />
      </span>
    </div>
  {:else}
    <div class="inline-grid text-xs font-medium w-full mt-1" style="grid-template-columns: auto repeat(4, auto); gap:2px 12px;">
      <span>Target XSS</span>
      <span class='text-center tabular-nums'>{formattedXSSDetails.xss}</span>
      <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedXSSDetails.xlss}</span>
      <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedXSSDetails.xhss}</span>
      <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedXSSDetails.xpss}</span>

      <span>Completed XSS</span>
      <span class='text-center tabular-nums'>{formattedCompletedXSS?.xss}</span>
      <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedCompletedXSS?.xlss}</span>
      <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedCompletedXSS?.xhss}</span>
      <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedCompletedXSS?.xpss}</span>

      <div />
      <div />
      <i class={cn('fa-solid ml-2 fa-lg',
        xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
        xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
        'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
      )} />
      <i class={cn('fa-solid ml-2 fa-lg',
        xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
        xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
        'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
      )} />
      <i class={cn('fa-solid ml-2 fa-lg',
        xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
        xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
        'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
      )} />
    </div>
  {/if}
</div>
